<script lang="ts">
  import BaseScene from "@engine/BaseScene.svelte";
  import creatures from "@config/data/creatures.json";

  export let unlockedIds: string[] = [];
  export let onBack: () => void;

  let selected: (typeof creatures)[number] | null = null;

  function isUnlocked(id: string): boolean {
    return unlockedIds.includes(id);
  }
</script>

<BaseScene sceneId="codex" ariaLabel="Ocean Codex" onEscape={onBack}>
  <div class="codex">
    <h1>Ocean Codex</h1>
    <p>Match a creature to unlock its page.</p>

    <div class="codex__grid">
      {#each creatures as c (c.id)}
        <button
          type="button"
          class="codex__tile"
          class:codex__tile--locked={!isUnlocked(c.id)}
          on:click={() => (selected = c)}
          aria-label={isUnlocked(c.id) ? c.displayName : "Locked creature"}
        >
          {#if isUnlocked(c.id)}
            <img src={c.spritePath} alt={c.displayName} loading="lazy" />
            <span>{c.displayName}</span>
          {:else}
            <span aria-hidden="true">?</span>
          {/if}
        </button>
      {/each}
    </div>

    {#if selected && isUnlocked(selected.id)}
      <div class="codex__detail" role="dialog" aria-modal="true" aria-label={selected.displayName}>
        <button type="button" class="codex__close" on:click={() => (selected = null)}>Close</button>
        <h2>{selected.displayName}</h2>
        <img src={selected.spritePath} alt={selected.displayName} />
        <p>{selected.factText}</p>
        <p class="codex__source">Source: {selected.factSourceId}</p>
      </div>
    {/if}

    <button type="button" class="codex__back" on:click={onBack}>Back</button>
  </div>
</BaseScene>

<style>
  .codex {
    max-width: 960px;
    margin: 0 auto;
    padding: 2rem 1rem;
  }
  .codex h1 {
    color: var(--color-heading);
    text-align: center;
    margin-bottom: 0.5rem;
  }
  .codex > p {
    text-align: center;
    color: var(--color-muted);
    margin-bottom: 1.5rem;
  }
  .codex__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 0.75rem;
  }
  .codex__tile {
    min-height: 100px;
    padding: 0.5rem;
    background: var(--color-surface);
    border: 1px solid var(--color-divider);
    border-radius: 0.5em;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
  }
  .codex__tile img {
    max-width: 60px;
    max-height: 60px;
  }
  .codex__tile span {
    font-size: 0.8rem;
    text-align: center;
  }
  .codex__tile--locked {
    opacity: 0.4;
    color: var(--color-muted);
    font-size: 2rem;
  }
  .codex__detail {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 5000;
  }
  .codex__detail img {
    max-width: 200px;
    max-height: 200px;
    margin: 1rem 0;
  }
  .codex__close {
    align-self: flex-end;
    min-width: 44px;
    min-height: 44px;
    background: transparent;
    color: white;
    border: 1px solid white;
    border-radius: 0.25em;
    cursor: pointer;
    padding: 0.5em 1em;
  }
  .codex__source {
    color: var(--color-muted);
    font-size: 0.9rem;
  }
  .codex__back {
    display: block;
    margin: 2rem auto 0;
    min-height: 44px;
    padding: 0.5em 1.5em;
    background: var(--color-surface);
    border: 1px solid var(--color-divider);
    border-radius: 0.5em;
    cursor: pointer;
  }
</style>
